"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplets, Waves, TriangleAlert as AlertTriangle, Flame, ShieldAlert, PhoneCall, CircleCheck as CheckCircle2, Clock, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
interface GuidanceStep {
  scenarioId: number;
  text: string;
}
interface ScenarioItem {
  id: number;
  title: string;
  badge: string;
  iconName: string;
  description: string;
  urgentNote: string;
  linkTo: string;
}
const scenarios: ScenarioItem[] = [{
  id: 1,
  title: "Water Damage",
  badge: "Immediate Action",
  iconName: "Droplets",
  description: "From ruptured supply lines, water heater bursts, or appliance leaks.",
  urgentNote: "Turn off the water supply main before water reaches living quarters.",
  linkTo: "/WaterDamageFloodRestorationPage"
}, {
  id: 2,
  title: "Flooding",
  badge: "Safety First",
  iconName: "Waves",
  description: "From storm surges, intense rain runoff, or natural drain overflows.",
  urgentNote: "Never touch or wade through deep floodwater containing submerged outlets.",
  linkTo: "/WaterDamageFloodRestorationPage"
}, {
  id: 3,
  title: "Sewage Backup",
  badge: "Biohazard Risk",
  iconName: "AlertTriangle",
  description: "From municipal line failure, toilet overflows, or main stack blockages.",
  urgentNote: "Keep family and pets completely separated from contaminated wet zones.",
  linkTo: "/SewageCleanupPage"
}, {
  id: 4,
  title: "Fire & Smoke Damage",
  badge: "Air Quality Alert",
  iconName: "Flame",
  description: "From kitchen incidents, electrical sparks, or structural furnace faults.",
  urgentNote: "Soot particulates travel through HVAC blowers into unaffected rooms.",
  linkTo: "/FireSmokeOdorRestorationPage"
}, {
  id: 5,
  title: "Mold Discovery",
  badge: "Containment Needed",
  iconName: "ShieldAlert",
  description: "From persistent plumbing drips, crawlspace dampness, or hidden condensation.",
  urgentNote: "Scrubbing or using fans on dry visible mold spreads spores across walls.",
  linkTo: "/MoldRemediationPage"
}];
const guidanceSteps: GuidanceStep[] = [{
  scenarioId: 1,
  text: "Shut off the water main or local valve if you can safely reach it."
}, {
  scenarioId: 1,
  text: "Move high-value items, rugs, and paperwork away from wet floors."
}, {
  scenarioId: 1,
  text: "Avoid using electrical devices, lamps, or wall outlets near pooled water."
}, {
  scenarioId: 1,
  text: "Contact TriServe immediately to begin structural moisture extraction."
}, {
  scenarioId: 2,
  text: "Evacuate the property immediately if instructed by local emergency officials."
}, {
  scenarioId: 2,
  text: "Do not enter standing indoor water where hidden hazards or electrical current may exist."
}, {
  scenarioId: 2,
  text: "Document visible exterior and interior waterlines with photos if safe."
}, {
  scenarioId: 2,
  text: "Call TriServe to dispatch heavy extraction equipment and drying systems."
}, {
  scenarioId: 3,
  text: "Do not enter or walk across sewage-affected rooms under any circumstances."
}, {
  scenarioId: 3,
  text: "Stop using all toilets, sinks, and drains in the building right away."
}, {
  scenarioId: 3,
  text: "Ventilate the area by opening exterior windows without running shared ductwork."
}, {
  scenarioId: 3,
  text: "Call TriServe for antimicrobial containment and regulated removal."
}, {
  scenarioId: 4,
  text: "Evacuate everyone immediately and do not re-enter until cleared by fire officials."
}, {
  scenarioId: 4,
  text: "Turn off forced-air heating or AC to avoid circulating soot and odors."
}, {
  scenarioId: 4,
  text: "Avoid wiping walls, fixtures, or upholstery, which grinds acidic soot deeper."
}, {
  scenarioId: 4,
  text: "Call TriServe to stabilize property openings and start thermal deodorization."
}, {
  scenarioId: 5,
  text: "Do not disturb, scrub, or scrape mold patches without protective containment."
}, {
  scenarioId: 5,
  text: "Turn off fans or forced-air systems blowing directly toward suspected fungal growth."
}, {
  scenarioId: 5,
  text: "Limit underlying moisture by fixing obvious plumbing drips if accessible."
}, {
  scenarioId: 5,
  text: "Contact TriServe for professional environmental testing and remediation."
}];
function ScenarioIcon({
  name
}: {
  name: string;
}) {
  switch (name) {
    case "Droplets":
      return <Droplets className="size-5 text-primary" />;
    case "Waves":
      return <Waves className="size-5 text-primary" />;
    case "AlertTriangle":
      return <AlertTriangle className="size-5 text-accent-foreground" />;
    case "Flame":
      return <Flame className="size-5 text-destructive" />;
    case "ShieldAlert":
      return <ShieldAlert className="size-5 text-secondary" />;
    default:
      return <Droplets className="size-5 text-primary" />;
  }
}
export default function Guidance() {
  const [activeTab, setActiveTab] = useState<number>(1);
  const shouldReduceMotion = useReducedMotion();
  const selectedScenario = scenarios.find(s => s.id === activeTab) || scenarios[0];
  const activeSteps = guidanceSteps.filter(s => s.scenarioId === activeTab);
  return <section id="emergencyscenarioguidance" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium text-xs">
            Quick Emergency Checklist
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground mb-4">
            What to do right away in property emergencies
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Take these basic precautions before our local dispatch team arrives on site. Prioritize personal safety over physical property restoration at all times.
          </p>
        </div>

        {/* Interactive Scenario Selector & Detail Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Scenario Tabs (Left / 5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
              Select an incident type
            </span>
            <div className="flex flex-col gap-2.5">
              {scenarios.map((scenario, i) => {
              const isActive = scenario.id === activeTab;
              return <Button key={scenario.id} data-index={i} type="button" onClick={() => setActiveTab(scenario.id)} className={`text-left w-full p-4 rounded-lg border transition-[border-color,box-shadow,background-color] flex items-center justify-between gap-4 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? "bg-card border-primary shadow-sm ring-1 ring-primary/20" : "bg-muted/40 border-border hover:bg-card hover:border-border"}`}>
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className={`size-10 rounded-md flex items-center justify-center shrink-0 ${isActive ? "bg-primary/10" : "bg-muted"}`}>
                        <ScenarioIcon name={scenario.iconName} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-foreground text-base tracking-tight truncate">
                          {scenario.title}
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {scenario.description}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className={`size-4 shrink-0 transition-transform ${isActive ? "text-primary translate-x-1" : "text-muted-foreground/40"}`} />
                  </Button>;
            })}
            </div>

            {/* Quick 24/7 Callout Card */}
            <div className="mt-4 p-5 rounded-xl bg-muted/60 border border-border flex items-start gap-4">
              <div className="size-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <PhoneCall className="size-4" />
              </div>
              <div className="text-sm">
                <div className="font-semibold text-foreground">Local Crews on 24/7 Standby</div>
                <p className="text-xs text-muted-foreground mt-1">
                  TriServe dispatches direct restoration teams throughout Maryland, DC, and Northern Virginia.
                </p>
              </div>
            </div>
          </div>

          {/* Active Detail Display (Right / 7 cols) */}
          <div className="lg:col-span-7">
            <motion.div key={selectedScenario.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: "easeOut"
          }} className="bg-card text-card-foreground border border-border rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col justify-between">
              <div>
                {/* Header within Active Card */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center">
                      <ScenarioIcon name={selectedScenario.iconName} />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block">
                        Scenario Guidance
                      </span>
                      <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                        {selectedScenario.title}
                      </h3>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs font-semibold px-2.5 py-1">
                    {selectedScenario.badge}
                  </Badge>
                </div>

                {/* Subtitle & Urgent Notice */}
                <div className="my-6">
                  <p className="text-sm sm:text-base text-foreground mb-4">
                    {selectedScenario.description}
                  </p>
                  <div className="p-3.5 rounded-lg bg-muted/70 border-l-4 border-l-primary flex items-center gap-3 text-xs sm:text-sm text-foreground">
                    <Clock className="size-4 text-primary shrink-0" />
                    <span>
                      <strong>Safety Priority:</strong> {selectedScenario.urgentNote}
                    </span>
                  </div>
                </div>

                {/* Numbered Safety Steps */}
                <div className="space-y-3.5 mb-8">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Recommended Immediate Steps
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {activeSteps.map((step, idx) => <div key={idx} data-index={idx} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-muted/30 border border-border/70">
                        <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs font-bold mt-0.5">
                          {idx + 1}
                        </div>
                        <p className="text-sm text-foreground leading-snug">
                          {step.text}
                        </p>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Bottom Actions */}
              <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Crews handle cleanup directly and coordinate with insurance</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={selectedScenario.linkTo}>Service Details</Link>
                  </Button>
                  <Button size="sm" asChild>
                    <Link to="/ContactRequestServicePage">Request Dispatch</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* 5 Scenario Quick-Reference Strip for Mobile or Print */}
        <div className="mt-12 pt-8 border-t border-border grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {scenarios.map((sc, i) => <div key={sc.id} data-index={i} className="p-3 rounded-lg bg-muted/30 border border-border text-center flex flex-col items-center gap-1">
              <span className="text-xs text-muted-foreground">Emergency 0{sc.id}</span>
              <span className="text-sm font-semibold text-foreground">{sc.title}</span>
            </div>)}
        </div>
      </div>
    </section>;
}